/**
 * CR412: Diagnostics Tab Content
 * 
 * Groups health banner, endpoint debug, and CR229 diagnostics into
 * a dedicated tab to keep the Preview tab clean.
 */

import { useMemo } from "react";
import { LivePreviewHealthBanner } from "@/components/admin/LivePreviewHealthBanner";
import { AdminDiagnosticsPanel, DataSourceBanner } from "@/components/admin/diagnostics";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import type { SiteState, PublishAuditEntry } from "@/hooks/useSiteState";
import type { PreviewMode } from "@/hooks/useAdminPreviewDeals";

interface DiagnosticsTabContentProps {
  siteState: SiteState | null;
  publishHistory: PublishAuditEntry[];
  currentMode: PreviewMode;
  dealsCount: number;
  stateLoading: boolean;
  previewLoading: boolean;
  onRefresh: () => void;
  endpointUrl: string;
  selectedBatchId: string;
  error: string | null;
  filteredCount: number;
  batchRunsCount: number;
  batchRunsLoading: boolean;
}

export function DiagnosticsTabContent({
  siteState,
  publishHistory,
  currentMode,
  dealsCount,
  stateLoading,
  previewLoading,
  onRefresh,
  endpointUrl,
  selectedBatchId,
  error,
  filteredCount,
  batchRunsCount,
  batchRunsLoading,
}: DiagnosticsTabContentProps) {
  return (
    <div className="space-y-4 pt-4">
      {/* System Health (always visible, compact) */}
      <LivePreviewHealthBanner defaultExpanded={false} />

      {/* Data Source Banner */}
      <DataSourceBanner
        environment="production"
        supabaseProjectRef={import.meta.env.VITE_SUPABASE_PROJECT_ID}
        activeSnapshotId={siteState?.active_snapshot_id || null}
        adminSnapshotId={siteState?.admin_snapshot_id || null}
        isLiveMode={currentMode === "live"}
      />

      <Accordion type="multiple" className="space-y-2">
        {/* Endpoint Debug */}
        <AccordionItem value="endpoint-debug" className="border rounded-lg">
          <AccordionTrigger className="px-4 py-3 text-sm hover:no-underline">
            <div className="flex items-center gap-2">
              <span>🔧 Endpoint Debug</span>
              <Badge variant="outline" className="text-[10px] h-4">
                {previewLoading ? "loading" : error ? "error" : "200 OK"}
              </Badge>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-mono">
              <div className="p-2 bg-background rounded border">
                <span className="text-muted-foreground">mode:</span>
                <span className="ml-2 font-bold text-primary">{currentMode}</span>
              </div>
              <div className="p-2 bg-background rounded border">
                <span className="text-muted-foreground">batch_run_id:</span>
                <span className="ml-2">{selectedBatchId?.slice(0, 12) || "—"}</span>
              </div>
              <div className="p-2 bg-background rounded border">
                <span className="text-muted-foreground">endpoint:</span>
                <span className="ml-2 text-blue-500">{endpointUrl}</span>
              </div>
              <div className="p-2 bg-background rounded border">
                <span className="text-muted-foreground">status:</span>
                <span className={`ml-2 ${previewLoading ? "text-yellow-500" : error ? "text-red-500" : "text-green-500"}`}>
                  {previewLoading ? "loading" : error ? "error" : "200 OK"}
                </span>
              </div>
              <div className="p-2 bg-background rounded border col-span-2">
                <span className="text-muted-foreground">deals returned:</span>
                <span className="ml-2 font-bold">{dealsCount}</span>
                <span className="ml-2 text-muted-foreground">(filtered: {filteredCount})</span>
              </div>
              <div className="p-2 bg-background rounded border col-span-2">
                <span className="text-muted-foreground">batch runs loaded:</span>
                <span className="ml-2 font-bold">{batchRunsCount}</span>
                {batchRunsLoading && <span className="ml-2 text-yellow-500">(loading...)</span>}
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* CR229 Diagnostics */}
        <AccordionItem value="cr229-diagnostics" className="border rounded-lg">
          <AccordionTrigger className="px-4 py-3 text-sm hover:no-underline">
            <div className="flex items-center gap-2">
              <span>CR229 Diagnostics</span>
              <Badge variant="outline" className="text-[10px] h-4">
                Live: {dealsCount} deals
              </Badge>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <AdminDiagnosticsPanel
              siteState={siteState}
              publishHistory={publishHistory}
              adminLiveDealsCount={currentMode === "live" ? dealsCount : (siteState?.published_deal_count ?? 0)}
              loading={stateLoading || previewLoading}
              onRefresh={onRefresh}
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
